import { useMemo } from 'react';
import { usePermissions } from 'context/PermissionsContext';
import { CONFIGURATION_PERMISSION_CODES } from 'permissions/constants/configurationPermissionCodes';
import { MODULE_KEY_MAP } from 'permissions/constants/moduleKeyMap';

export { CONFIGURATION_PERMISSION_CODES };

const usePermissionsConfiguration = () => {
  const { permissionsLoading, hasPermission, hasModuleAccess } =
    usePermissions();

  return useMemo(
    () => ({
      loading: permissionsLoading,
      canAccessModule: hasModuleAccess(MODULE_KEY_MAP.SETTINGS),
      canViewSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.VIEW_SECTION
      ),
      canAccessCompany: hasPermission(CONFIGURATION_PERMISSION_CODES.COMPANY),
      canViewCompanySection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.COMPANY_VIEW_SECTION
      ),
      canEditRegionalConfig: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EDIT_REGIONAL_CONFIG
      ),
      canCreateBranch: hasPermission(
        CONFIGURATION_PERMISSION_CODES.CREATE_BRANCH
      ),
      canEditBranch: hasPermission(CONFIGURATION_PERMISSION_CODES.EDIT_BRANCH),
      canDeleteBranch: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DELETE_BRANCH
      ),
      canAccessUsers: hasPermission(CONFIGURATION_PERMISSION_CODES.USERS),
      canViewUsersSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.USERS_VIEW_SECTION
      ),
      canCreateUser: hasPermission(CONFIGURATION_PERMISSION_CODES.CREATE_USER),
      canEditUser: hasPermission(CONFIGURATION_PERMISSION_CODES.EDIT_USER),
      canDeleteUser: hasPermission(CONFIGURATION_PERMISSION_CODES.DELETE_USER),
      canAccessProfilesAndRoles: hasPermission(
        CONFIGURATION_PERMISSION_CODES.PROFILES_AND_ROLES
      ),
      canViewProfilesAndRolesSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.PROFILES_AND_ROLES_VIEW_SECTION
      ),
      canCreateRole: hasPermission(CONFIGURATION_PERMISSION_CODES.CREATE_ROLE),
      canEditRole: hasPermission(CONFIGURATION_PERMISSION_CODES.EDIT_ROLE),
      canDeleteRole: hasPermission(CONFIGURATION_PERMISSION_CODES.DELETE_ROLE),
      canAccessDataManagement: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DATA_MANAGEMENT
      ),
      canViewDataManagementSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DATA_MANAGEMENT_VIEW_SECTION
      ),
      canAccessAutoRotation: hasPermission(
        CONFIGURATION_PERMISSION_CODES.AUTO_ROTATION
      ),
      canViewAutoRotationSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.AUTO_ROTATION_VIEW_SECTION
      ),
      canExportImport: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EXPORT_IMPORT
      ),
      canEditAutoRotation: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EDIT_AUTO_ROTATION
      ),
      canAccessProtocols: hasPermission(
        CONFIGURATION_PERMISSION_CODES.PROTOCOLS
      ),
      canViewProtocolsSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.PROTOCOLS_VIEW_SECTION
      ),
      canAccessPrompts: hasPermission(
        CONFIGURATION_PERMISSION_CODES.ACCESS_PROMPTS
      ),
      canCreateProtocol: hasPermission(
        CONFIGURATION_PERMISSION_CODES.CREATE_PROTOCOL
      ),
      canViewProtocol: hasPermission(
        CONFIGURATION_PERMISSION_CODES.VIEW_PROTOCOL
      ),
      canEditProtocol: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EDIT_PROTOCOL
      ),
      canDeleteProtocol: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DELETE_PROTOCOL
      ),
      canAccessDocuments: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DOCUMENTS
      ),
      canViewDocumentsSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DOCUMENTS_VIEW_SECTION
      ),
      canCreateDocument: hasPermission(
        CONFIGURATION_PERMISSION_CODES.CREATE_DOCUMENT
      ),
      canViewDocument: hasPermission(
        CONFIGURATION_PERMISSION_CODES.VIEW_DOCUMENT
      ),
      canEditDocument: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EDIT_DOCUMENT
      ),
      canDeleteDocument: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DELETE_DOCUMENT
      ),
      canAccessPaymentsAndSubscriptions: hasPermission(
        CONFIGURATION_PERMISSION_CODES.PAYMENTS_AND_SUBSCRIPTIONS
      ),
      canViewPaymentsSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.PAYMENTS_VIEW_SECTION
      ),
      canSetDefaultPayment: hasPermission(
        CONFIGURATION_PERMISSION_CODES.SET_DEFAULT_PAYMENT
      ),
      canAddPaymentMethod: hasPermission(
        CONFIGURATION_PERMISSION_CODES.ADD_PAYMENT_METHOD
      ),
      canEditPaymentMethod: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EDIT_PAYMENT_METHOD
      ),
      canDeletePaymentMethod: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DELETE_PAYMENT_METHOD
      ),
      canChangePlan: hasPermission(CONFIGURATION_PERMISSION_CODES.CHANGE_PLAN),
      canCancelSubscription: hasPermission(
        CONFIGURATION_PERMISSION_CODES.CANCEL_SUBSCRIPTION
      ),
      canAccessAuditLog: hasPermission(
        CONFIGURATION_PERMISSION_CODES.AUDIT_LOG
      ),
      canViewAuditLogSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.AUDIT_LOG_VIEW_SECTION
      ),
      canAccessSubstitutions: hasPermission(
        CONFIGURATION_PERMISSION_CODES.SUBSTITUTION
      ),
      canViewSubstitutionsSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.SUBSTITUTION_VIEW_SECTION
      ),
      canCreateSubstitution: hasPermission(
        CONFIGURATION_PERMISSION_CODES.CREATE_SUBSTITUTION
      ),
      canEditSubstitution: hasPermission(
        CONFIGURATION_PERMISSION_CODES.EDIT_SUBSTITUTION
      ),
      canDeleteSubstitution: hasPermission(
        CONFIGURATION_PERMISSION_CODES.DELETE_SUBSTITUTION
      ),
      canAccessIntegration: hasPermission(
        CONFIGURATION_PERMISSION_CODES.INTEGRATION
      ),
      canViewIntegrationSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.INTEGRATION_VIEW_SECTION
      ),
      canAccessLogLogin: hasPermission(
        CONFIGURATION_PERMISSION_CODES.LOG_LOGIN
      ),
      canViewLogLoginSection: hasPermission(
        CONFIGURATION_PERMISSION_CODES.LOG_LOGIN_VIEW_SECTION
      ),
    }),
    [permissionsLoading, hasPermission, hasModuleAccess]
  );
};

export default usePermissionsConfiguration;
